import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { SquarePen, Trash2, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getAllMenusPermissions, deleteMenu } from '../../Services/RolesPermissions/MenuManagemnet/GetMenusPermissions';
import DeleteModal from '../../Components/Modals/DeleteModal';
import PermissionWrapper from '../../Components/Auth/PermissionWrapper';
import { TextField, InputAdornment } from '@mui/material';
import GlobalPagination from '../../Components/Common/Globalpagination';

const GetMenusPermissions = () => {
    const navigate = useNavigate();
    const [menusData, setMenusData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [limit, setLimit] = useState(10);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    const fetchMenusData = useCallback(async (searchQuery = '', currentPage = 1, currentLimit = limit) => {
        try {
            setLoading(true);
            const limitParam = currentLimit !== 10 ? currentLimit : undefined;
            const response = await getAllMenusPermissions(currentPage, limitParam, searchQuery);

            if (response && response.success) {
                setMenusData(response.data || []);
                setTotalPages(response.totalPages || 1);
                setTotalRecords(response.totalRecords ?? response.total ?? ((response.totalPages || 1) * currentLimit));
            } else if (Array.isArray(response)) {
                setMenusData(response);
                setTotalPages(1);
                setTotalRecords(response.length);
            } else {
                setMenusData([]);
                setTotalPages(1);
                setTotalRecords(0);
            }
        } catch (err) {
            console.error(err);
            setError("Failed to fetch menus. Please try again later.");
        } finally {
            setLoading(false);
        }
    }, [limit]);

    useEffect(() => {
        fetchMenusData(searchTerm, page, limit);
    }, [fetchMenusData, searchTerm, page, limit]);

    const handleSearchChange = (event) => {
        const value = event.target.value;
        setSearchTerm(value);
        setPage(1);
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const handleDelete = (id) => {
        setItemToDelete(id);
        setDeleteError(null);
        setDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!itemToDelete) return;
        try {
            setIsDeleting(true);
            setDeleteError(null);
            const response = await deleteMenu(itemToDelete);
            if (response && response.success) {
                fetchMenusData(searchTerm, page); // Refresh the list
                setDeleteModalOpen(false);
                setItemToDelete(null);
            } else {
                setDeleteError(response?.message || "Failed to delete menu.");
            }
        } catch (err) {
            console.error(err);
            const errorMessage = err.response?.data?.message || err.message || "Failed to delete menu.";
            setDeleteError(errorMessage);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleEdit = (id) => {
        navigate(`/menu-permissions/edit/${id}`);
    };

    return (
        <PermissionWrapper permission="view">
            <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                    <Typography sx={{
                        color: '#222',
                        fontSize: { xs: '1.75rem', md: '1.8rem' },
                        fontWeight: 500
                    }}>
                        Menu Permissions
                    </Typography>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1.5 }}>
                        <TextField
                            size="small"
                            placeholder="Search menus..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start" sx={{ mr: 0.5 }}>
                                        <Search size={16} color="#666" />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                width: { xs: '100%', sm: '220px', md: '300px' },
                                bgcolor: 'white',
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '6px',
                                    height: '40px',
                                    fontSize: '0.85rem',
                                    '& fieldset': { borderColor: '#E0E0E0' },
                                    '&:hover fieldset': { borderColor: '#BDBDBD' },
                                    '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                }
                            }}
                        />
                    </Stack>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                <Paper sx={{ borderRadius: "8px", border: "1px solid #E8E8E8", overflow: "hidden" }} elevation={0}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                    <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', textAlign: 'center', width: '60px' }}>S.No</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '200px' }}>Menu Name</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Slug</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '150px' }}>Module Type</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Active</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '100px', textAlign: 'right' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                                            <CircularProgress size={24} sx={{ color: '#FFBD65' }} />
                                        </TableCell>
                                    </TableRow>
                                ) : menusData.length > 0 ? (
                                    menusData.map((menu, idx) => (
                                        <TableRow key={menu.id || idx} sx={{
                                            transition: 'background-color 0.2s',
                                            '& td': { borderBottom: '1px solid #F0F0F0' },
                                            '&:last-child td': { borderBottom: 0 },
                                        }}>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#666', textAlign: 'center' }}>{idx + 1}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#333', fontWeight: 500 }}>{menu.name || 'N/A'}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#555' }}>{menu.slug || 'N/A'}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#555' }}>{menu.moduleType || '-'}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5 }}>
                                                <Box
                                                    sx={{
                                                        display: 'inline-block',
                                                        px: 1.5, py: 0.3,
                                                        borderRadius: '4px',
                                                        bgcolor: menu.isActive ? '#E8F5E8' : '#FFEBEE',
                                                        color: menu.isActive ? '#2E7D32' : '#C62828',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 600
                                                    }}
                                                >
                                                    {menu.isActive ? 'Active' : 'Inactive'}
                                                </Box>
                                            </TableCell>
                                            <TableCell align="right" sx={{ py: 1.25, px: 1.5 }}>
                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                    <PermissionWrapper permission="edit" mode="disable">
                                                        <Tooltip title="Edit">
                                                            <IconButton disabled disableFocusRipple disableRipple disableTouchRipple
                                                                onClick={() => handleEdit(menu.id)}
                                                                sx={{ color: '#FFBD65' }}
                                                                size="small"
                                                            >
                                                                <SquarePen size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                    <PermissionWrapper permission="delete" mode="disable">
                                                        <Tooltip title="Delete">
                                                            <IconButton disabled disableFocusRipple disableRipple disableTouchRipple
                                                                onClick={() => handleDelete(menu.id)}
                                                                sx={{ color: '#FF4D49' }}
                                                                size="small"
                                                            >
                                                                <Trash2 size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ py: 4, color: '#AAA', fontSize: '0.8rem' }}>
                                            No menus found
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <GlobalPagination
                        page={page}
                        pageSize={limit}
                        totalRecords={totalRecords}
                        totalPages={totalPages}
                        onPageChange={(newPage) => setPage(newPage)}
                        onPageSizeChange={(newLimit) => {
                            setLimit(newLimit);
                            setPage(1);
                        }}
                        loading={loading}
                    />
                </Paper>
                <DeleteModal
                    open={deleteModalOpen}
                    onClose={() => setDeleteModalOpen(false)}
                    onDelete={confirmDelete}
                    title="Delete Menu"
                    description="Are you sure you want to delete this menu? This action cannot be undone."
                    error={deleteError}
                    loading={isDeleting}
                />
            </Box>
        </PermissionWrapper>
    );
};

export default GetMenusPermissions;
